import { Box, Typography, useTheme } from "@mui/material";
import { RetroButton } from "components/RetroButton";
import { RetroCard } from "components/RetroCard";
import { abbreviateNumber, copyToClipboard, toFixedIfNecessary } from "utils";
import { Group, Help, Telegram } from "@mui/icons-material";
import { ScrollableBox } from "components/ScrollableBox";
import { LoadMoreRow } from "components/LoadMoreRow";
import { LoadingBlock } from "components/LoadingScreen";
import { Card, Col, Row } from "v2/components/base/Flex/ui";
import SubPageLayout from "v2/layouts/SubPageLayout";
import { ROUTES } from "consts";
import { useNavigate } from "react-router-dom";
import Divider from "v2/components/base/Divider/ui";
import { Value } from "components/Value";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { useState } from "react";
import { useScript } from "usehooks-ts";
import useUserStoreV2 from "store/user-store-v2/useUserStoreV2";
import { useNavigatePreserveQuery } from "lib/hooks/useNavigatePreserveQuery";
import useToastStore from "store/toast-store/useToastStore";
import axiosService from "services/axios";
import { useQuery } from "@tanstack/react-query";
import { useInViewInfiniteQuery } from "hooks/useInViewInfiniteQuery";
import BasicButton from "v2/components/Buttons/BasicButton";
import Tabs from "v2/components/base/Tabs/ui";
import useTabs from "v2/components/base/Tabs/hooks/useTabs";
import Skeleton from "v2/components/base/Skeleton/ui";
import Model from "v2/components/base/Model/ui";
import TweetButton from "v2/components/TwitterShareButton/TwitterShareButton";
import useLayoutConfigStore from "store/layout-config-store/useLayoutConfigStore";

const ReferralTabs = ["all", "user", "group"];

export const ReferralPage = () => {
  const theme = useTheme();

  const handleBack = () => {
    navigate(ROUTES.portfolio);
  };

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isHowItWorksModalOpen, setIsHowItWorksModalOpen] = useState(
    !localStorage.getItem("isSkipHowItWorksModalOpen"),
  );
  const [page, setPage] = useState(1);

  const { tgUserId } = useUserStoreV2();
  useScript("https://platform.twitter.com/widgets.js");
  const navigate = useNavigatePreserveQuery();

  const twitterShareButtonRef = useRef<HTMLAnchorElement>(null);
  const { showToast } = useToastStore();
  const { setIsShowFooter } = useLayoutConfigStore();

  const {
    data: referralOverview = {
      summary: {
        totalReferralShills: 0,
        userReferralCount: 0,
        groupReferralCount: 0,
      },
      referralShills: [],
    },
    isLoading: isReferralOverviewLoading,
  } = useQuery({
    queryKey: ["referralOverview", tgUserId],
    queryFn: () => axiosService.getReferralShillsOverviewByUserId({ userId: tgUserId! }),
    enabled: !!tgUserId,
  });

  const {
    summary = {
      totalReferralShills: 0,
      userReferralCount: 0,
      groupReferralCount: 0,
    },
    referralShills = [],
  } = referralOverview;

  const { totalReferralShills, userReferralCount, groupReferralCount } = summary;
  const userReferral = useMemo(
    () => referralShills.filter((item: any) => item.type === "User"),
    [referralShills],
  );
  const groupReferral = useMemo(
    () => referralShills.filter((item: any) => item.type === "Group"),
    [referralShills],
  );
  const { selectedTab, handleTabChange } = useTabs(0);

  const navigateToPortfolio = () => {
    navigate(ROUTES.portfolio);
  };

  const copyReferralLink = () => {
    copyToClipboard(`https://app.cabal.town?referralId=${tgUserId}`);
    showToast({
      message: "Referral link copied to clipboard",
      variant: "success",
    });
  };

  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
  };

  useEffect(() => {
    setIsShowFooter(false);

    return () => {
      setIsShowFooter(true);
    };
  }, []);

  return (
    <>
      <SubPageLayout handleBack={handleBack} backToTitle="Back">
        <Col type="bc" sx={{ overflowY: "scroll" }} fullWidth fullHeight>
          <Col sx={{ marginBottom: "24px" }} fullWidth fullHeight>
            <Row type="bc" marginBottom="12px" fullWidth>
              <Typography variant="mobile-body-m-heavy">Referral Stats</Typography>
            </Row>
            <Card.Section
              sx={{ background: theme.palette.surface["bg"], width: "100%", height: "100%" }}>
              <Col fullWidth fullHeight>
                <Row type="bc" sx={{ padding: "9px" }} fullWidth fullHeight>
                  <Typography variant="mobile-body-m">Lifetime Earning</Typography>
                  <Typography variant="mobile-body-m">
                    <Value
                      variant="mobile-display-s"
                      value={
                        totalReferralShills > 0
                          ? `${toFixedIfNecessary(totalReferralShills.toString(), 0)}`
                          : "0"
                      }
                    />{" "}
                    <Typography variant="mobile-display-s">SHILL</Typography>
                  </Typography>
                </Row>
                <Divider.Row />
                <Row type="bc" sx={{ padding: "9px" }} fullWidth fullHeight>
                  <Typography variant="mobile-body-m">Users</Typography>
                  <Typography variant="mobile-body-m">
                    <Value variant="mobile-display-s" value={userReferralCount} />
                  </Typography>
                </Row>
                <Divider.Row />
                <Row type="bc" sx={{ padding: "9px" }} fullWidth fullHeight>
                  <Typography variant="mobile-body-m">Groups</Typography>
                  <Typography variant="mobile-body-m">
                    <Value variant="mobile-display-s" value={groupReferralCount} />
                  </Typography>
                </Row>
              </Col>
            </Card.Section>

            <Row type="sc" marginBottom="12px" fullWidth marginTop="23px" gap="8px">
              <Typography variant="mobile-body-m-heavy">Your Referrals</Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  border: `1px solid ${theme.palette.border.dark}`,
                  borderRadius: "8px",
                  padding: "2px 6px",
                  bgcolor: theme.palette.surface["container-bg"],
                  gap: "4px",
                }}>
                <Group fontSize="small" />
                <Typography variant="body-sm" color="text.secondary">
                  {userReferralCount + groupReferralCount}
                </Typography>
              </Box>
            </Row>
            <Card.Section
              sx={{
                background: theme.palette.surface["bg"],
                width: "100%",
                height: "100%",
                minHeight: "240px",
                marginBottom: "24px",
              }}>
              <Tabs.Group
                tabs={["All", "User", "Group"]}
                selectedTab={selectedTab}
                handleTabChange={handleTabChange}
                sx={{ width: "100%", maxWidth: "330px", marginX: "auto" }}
              />
              <Row marginY="8px" />
              <Tabs.Body
                index={0}
                selectedTab={selectedTab}
                sx={{
                  width: "100%",
                  height: "100%",
                  minHeight: "fit-content",
                  display: selectedTab === 0 ? "flex" : "none",
                  overflowY: "scroll",
                  gap: "8px",
                }}>
                <Skeleton.Logo
                  isLoading={isReferralOverviewLoading}
                  sx={{ width: "100%", minHeight: "800px" }}>
                  {referralShills?.length > 0 ? (
                    referralShills.map((item: any, index: number) => {
                      const { shillsEarned, referredName } = item;
                      return (
                        <Card.Item key={index} sx={{ width: "100%", padding: "16px" }}>
                          <Row type="bc" fullWidth>
                            <Typography variant="mobile-body-s-heavy">{referredName}</Typography>
                            <Typography variant="mobile-body-s">
                              {shillsEarned > 0
                                ? `${toFixedIfNecessary(shillsEarned.toString(), 0)}`
                                : "0"}{" "}
                              SHILL
                            </Typography>
                          </Row>
                        </Card.Item>
                      );
                    })
                  ) : selectedTab !== 2 ? (
                    <AllEmptyView
                      onInviteNow={() => {
                        setIsShareModalOpen(true);
                        setIsHowItWorksModalOpen(false);
                      }}
                    />
                  ) : (
                    <GroupEmptyView />
                  )}
                </Skeleton.Logo>
              </Tabs.Body>
              <Tabs.Body
                index={1}
                selectedTab={selectedTab}
                sx={{
                  width: "100%",
                  height: "100%",
                  minHeight: "fit-content",
                  display: selectedTab === 1 ? "flex" : "none",
                  overflowY: "scroll",
                  gap: "8px",
                }}>
                <Skeleton.Logo
                  isLoading={isReferralOverviewLoading}
                  sx={{ width: "100%", height: "40px" }}>
                  {userReferral?.length > 0 ? (
                    userReferral.map((item: any, index: number) => {
                      const { shillsEarned, referredName } = item;
                      return (
                        <Card.Item key={index} sx={{ width: "100%", padding: "16px" }}>
                          <Row type="bc" fullWidth>
                            <Typography variant="mobile-body-s-heavy">{referredName}</Typography>
                            <Typography variant="mobile-body-s">
                              {shillsEarned > 0
                                ? `${toFixedIfNecessary(shillsEarned.toString(), 0)}`
                                : "0"}{" "}
                              SHILL
                            </Typography>
                          </Row>
                        </Card.Item>
                      );
                    })
                  ) : selectedTab !== 2 ? (
                    <AllEmptyView
                      onInviteNow={() => {
                        setIsShareModalOpen(true);
                        setIsHowItWorksModalOpen(false);
                      }}
                    />
                  ) : (
                    <GroupEmptyView />
                  )}
                </Skeleton.Logo>
              </Tabs.Body>
              <Tabs.Body
                index={2}
                selectedTab={selectedTab}
                sx={{
                  width: "100%",
                  height: "100%",
                  minHeight: "272px",
                  display: selectedTab === 2 ? "flex" : "none",
                  overflowY: "scroll",
                  gap: "8px",
                }}>
                <Skeleton.Logo
                  isLoading={isReferralOverviewLoading}
                  sx={{ width: "100%", height: "40px" }}>
                  {groupReferral?.length > 0 ? (
                    groupReferral.map((item: any, index: number) => {
                      const { shillsEarned, referredName } = item;
                      return (
                        <Card.Item key={index} sx={{ width: "100%", padding: "16px" }}>
                          <Row type="bc" fullWidth>
                            <Typography variant="mobile-body-s-heavy">{referredName}</Typography>
                            <Typography variant="mobile-body-s">
                              {shillsEarned > 0
                                ? `${toFixedIfNecessary(shillsEarned.toString(), 0)}`
                                : "0"}{" "}
                              SHILL
                            </Typography>
                          </Row>
                        </Card.Item>
                      );
                    })
                  ) : selectedTab !== 2 ? (
                    <AllEmptyView
                      onInviteNow={() => {
                        setIsShareModalOpen(true);
                        setIsHowItWorksModalOpen(false);
                      }}
                    />
                  ) : (
                    <GroupEmptyView />
                  )}
                </Skeleton.Logo>
              </Tabs.Body>
            </Card.Section>
          </Col>
          <Row type="bc" gap="8px" fullWidth sx={{ marginBottom: "42px" }}>
            <BasicButton variant="white" sx={{ width: "100%" }} onClick={copyReferralLink}>
              <Typography variant="mobile-action-s-heavy">Copy</Typography>
            </BasicButton>
            <BasicButton
              variant="primary"
              sx={{ width: "100%" }}
              onClick={() => setIsShareModalOpen(true)}>
              <Typography variant="mobile-action-s-heavy">Share</Typography>
            </BasicButton>
          </Row>
          {/* <Col sx={{height: "100px"}} fullWidth/> */}
        </Col>
      </SubPageLayout>
      <Model.Wrapper open={isShareModalOpen} onClose={handleCloseShareModal}>
        <Model.Container size="md">
          <Model.Header.Wrapper>
            <Model.Header.Title></Model.Header.Title>
            <Model.Header.CloseButton close={handleCloseShareModal} />
          </Model.Header.Wrapper>
          <Model.Content>
            <Col type="cc" sx={{ gap: "16px", marginBottom: "12px" }} fullWidth>
              {/* <Box display={"flex"} justifyContent={"center"}>
                <img src={InveiteIcon} alt="icon" />
              </Box> */}
              <Box
                display={"flex"}
                flexDirection={"column"}
                alignItems={"center"}
                textAlign={"center"}
                sx={{ gap: "24px" }}>
                <Typography variant="mobile-h3">INVITE YOUR FRIENDS</Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <Typography variant="mobile-body-s" color={theme.palette.text.secondary}>
                    Earn 1% of SHILLs from groups and 10% from users
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  gap: "24px",
                  width: "fit-content",
                  margin: "12px auto 0",
                }}>
                <Box sx={{ margin: "0 auto" }}>
                  {/* <RetroButton> */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "4px",
                    }}>
                    {/* <Typography variant="title-h3">SHARE ON </Typography> */}
                    {/* <X fontSize="small"/> */}
                    {/* <a
                      ref={twitterShareButtonRef}
                      href="https://twitter.com/share?ref_src=twsrc%5Etfw"
                      className="twitter-share-button"
                      data-size="large"
                      data-text="Join me at Cabal Town.  Earn up to 50% of the fees from your trading cabals!"
                      data-url={`https://app.cabal.town?referralId=${tgUserId}`}
                      data-show-count="false">
                      Share
                    </a> */}
                    <TweetButton tgUserId={tgUserId || null} />
                  </Box>
                  {/* </RetroButton> */}
                </Box>
                <Box sx={{ margin: "0 auto" }}>
                  {/* <RetroButton> */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "4px",
                    }}>
                    {/* <Typography variant="title-h3">Share </Typography> */}
                    <a
                      href={`https://t.me/share/url?url=${`https://app.cabal.town?referralId=${tgUserId}`}&text=${"Join me at Cabal Town.  Earn up to 50% of the fees from your trading cabals!"}`}
                      target="_blank"
                      style={{
                        borderRadius: "100px",
                        backgroundColor: "#2481cc",
                        color: "#fff",
                        padding: "2px 12px",
                        font: "normal normal normal 12px/18px 'Helvetica Neue',Arial,sans-serif",
                        fontWeight: "500",
                        textDecoration: "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "4px",
                        height: "28px",
                      }}>
                      <Telegram fontSize="small" />
                      <b>Share</b>
                    </a>
                    {/* <script async src="https://telegram.org/js/telegram-widget.js?22" data-telegram-share-url="https://t.me/CabalTown_Bot " data-comment="Join me in the Cabal Town. Trade together and earn up to 50% trade fees" data-size="large"></script> */}
                  </Box>
                  {/* </RetroButton> */}
                </Box>
              </Box>
            </Col>
          </Model.Content>
        </Model.Container>
      </Model.Wrapper>
    </>
  );
};

const AllEmptyView = ({ onInviteNow }: { onInviteNow: () => void }) => {
  return (
    <Col
      type="cc"
      sx={{
        gap: "16px",
        margin: "0px auto",
        padding: "28px 16px",
      }}>
      <Typography variant="mobile-h3">NO REFERRALS</Typography>
      <Typography variant="mobile-body-s">You have no referrals yet</Typography>
      <Box>
        <BasicButton variant="primary" sx={{ width: "100%" }} onClick={onInviteNow}>
          <Typography variant="mobile-action-s-heavy">Invite Now</Typography>
        </BasicButton>
      </Box>
    </Col>
  );
};

const GroupEmptyView = () => {
  return (
    <Col
      type="cc"
      sx={{
        gap: "8px",
        margin: "0px auto",
        padding: "28px 16px",
      }}>
      <Typography variant="mobile-h3">No Referral Groups</Typography>
      <Typography variant="mobile-body-s">You have no referred groups yet</Typography>
    </Col>
  );
};
